import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { collectChecklistGaps } from "./checklistResponseConfig";

function scrollToChecklistItem(item) {
  if (item?.id == null || typeof document === "undefined") return;
  const node = document.querySelector(
    `[data-checklist-item-id="${CSS.escape(String(item.id))}"]`
  );
  node?.scrollIntoView({ behavior: "smooth", block: "center" });
}

/**
 * Sticky overview of remaining checklist gaps. Used by compact V2/V3 so every
 * host (service report, quality, work orders, drawings fill) sees the same bar.
 */
export default function ChecklistGapsBar({
  items,
  stickyTopClassName = "top-[var(--dashboard-topbar-height,4rem)]",
  /** sticky = in-flow pin. static = inside fixed/sticky page chrome. fixed = pin to viewport. */
  position = "sticky",
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const summary = useMemo(() => collectChecklistGaps(items), [items]);
  const { gaps, unanswered, documents, fields, resolvedCount, totalCount } = summary;

  if (totalCount === 0) return null;

  const complete = gaps.length === 0;

  return (
    <div
      className={clsx(
        "rounded-xl border px-2.5 py-1.5 shadow-sm backdrop-blur-sm",
        position === "sticky" &&
          "sticky z-20 -mx-1 mb-1 sm:-mx-0",
        position === "sticky" && stickyTopClassName,
        position === "static" && "mb-0",
        position === "fixed" && "pointer-events-auto",
        complete
          ? "border-emerald-200 bg-emerald-50/95"
          : "border-amber-200 bg-amber-50/95"
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex min-w-0 items-center gap-2">
        {complete ? (
          <CheckIcon className="h-4 w-4 shrink-0 text-emerald-700" aria-hidden />
        ) : (
          <ExclamationTriangleIcon
            className="h-4 w-4 shrink-0 text-amber-700"
            aria-hidden
          />
        )}
        <div className="min-w-0 flex-1 text-xs font-semibold leading-tight text-slate-800">
          {complete
            ? t("quality.checklist.gaps.complete", {
                count: totalCount,
                resolved: resolvedCount,
                total: totalCount,
              })
            : t("quality.checklist.gaps.remaining", {
                count: gaps.length,
                resolved: resolvedCount,
                total: totalCount,
              })}
        </div>
        {!complete && (
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="inline-flex min-h-8 shrink-0 items-center gap-0.5 rounded-full bg-white px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200"
            aria-expanded={open}
          >
            {t("quality.checklist.gaps.toggle")}
            {open ? (
              <ChevronUpIcon className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>
        )}
      </div>

      {open && !complete && (
        <ul className="mt-1.5 max-h-40 space-y-1 overflow-y-auto border-t border-amber-200/80 pt-1.5">
          {fields.map((item) => (
            <li key={`f-${item.id || item.title}`}>
              <button type="button" onClick={() => scrollToChecklistItem(item)} className="w-full truncate text-left text-xs text-slate-700">
                {item.title || t("quality.checklist.gaps.untitled")}
                <span className="ml-1 text-amber-800">{t("quality.checklist.gaps.fields")}</span>
              </button>
            </li>
          ))}
          {unanswered.map((item) => (
            <li key={`u-${item.id || item.title}`}>
              <button
                type="button"
                onClick={() => scrollToChecklistItem(item)}
                className="w-full truncate text-left text-xs text-slate-700 hover:text-slate-900"
              >
                {item.title || t("quality.checklist.gaps.untitled")}
                <span className="ml-1 text-[10px] font-medium uppercase text-amber-800">
                  {t("quality.checklist.gaps.unanswered")}
                </span>
              </button>
            </li>
          ))}
          {documents.map((item) => (
            <li key={`d-${item.id || item.title}`}>
              <button
                type="button"
                onClick={() => scrollToChecklistItem(item)}
                className="w-full truncate text-left text-xs text-slate-700 hover:text-slate-900"
              >
                {item.title || t("quality.checklist.gaps.untitled")}
                <span className="ml-1 text-[10px] font-medium uppercase text-amber-800">
                  {t("quality.checklist.gaps.documents")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
